import isEmpty from "lodash/isEmpty";

import { IPeopleName } from "@interfaces/people.interface";
import { UnrecognizedUser, User, UserForDropdown } from "@app-types/common.type";
import { getDomainFromEmail } from "@utils/strings.util";

export const getFullNameOrEmail = (
  object: { first_name?: string | null; last_name?: string | null; email?: string | null }
): string => {
  const firstName = object.first_name?.trim();
  const lastName = object.last_name?.trim();
  if (firstName || lastName) {
    return `${firstName ?? ""} ${lastName ?? ""}`.trim();
  }
  return object.email || "";
};

export function getUserInitials(user: User | UnrecognizedUser | IPeopleName): string {
  if (isEmpty(user)) return "";

  const { first_name, last_name, email } = user as Partial<IPeopleName & User & UnrecognizedUser>;

  if (first_name?.trim() || last_name?.trim()) {
    return `${first_name?.charAt(0) || ""}${last_name?.charAt(0) || ""}`.toUpperCase();
  }

  // If the user has no first or last name, use the email to generate initials
  if (email) {
    const emailParts = email.split("@")[0].split(/[._-]/).filter(Boolean);
    if (emailParts.length >= 2) {
      return `${emailParts[0].charAt(0)}${emailParts[1].charAt(0)}`.toUpperCase();
    }
    return emailParts[0]?.charAt(0)?.toUpperCase() || "";
  }

  return "";
}

export const convertUsersToUsersForDropdown = (
  users: User[],
  activeUser: User
): UserForDropdown[] => {
  if (!users || !activeUser) return [];

  const parsedUsers: UserForDropdown[] =
    users?.map((user) => ({
      ...user,
      value: user.id || "",
      label: user.full_name || "",
      type: "team members",
      title:
        user.id === activeUser?.id
          ? activeUser.title
            ? { ...activeUser.title, title: activeUser.title.title + " (Me)" }
            : { title: "(Me)", id: "" }
          : user.title || null,
    })) || [];

  const userMe = parsedUsers.find((i) => i.id === activeUser?.id);

  if (!userMe) {
    return [...parsedUsers];
  }

  return [userMe, ...parsedUsers.filter((i) => i.id !== userMe.id)] as UserForDropdown[];
};

/**
 * Determines whether a member should be treated as an "inactive member."
 *
 * - If the backend explicitly sets is_active to false → inactive.
 * - For internal members whose email domain matches org domains but whose
 *   status is not 'Active' → inactive (covers Pending / deactivated org members).
 * - External attendees are never marked inactive — they lack org membership status.
 */
export function isInactiveMember(
  member: { email?: string; external?: boolean; is_active?: boolean; status?: string },
  orgDomains: string[] | undefined
): boolean {
  if (member?.is_active === false) return true;

  const isExternal = member?.external ?? false;
  if (isExternal) return false;

  const memberDomain = getDomainFromEmail(member?.email || "");
  const isDomainMatched = orgDomains?.some((domain) => domain === memberDomain) ?? false;
  return isDomainMatched && !!member?.status && member.status !== "Active";
}

/**
 * Whether the member should receive highlighted (teal) styling.
 * Internal members are always highlighted; external members only when their
 * email domain is listed in organization.domains.
 */
export function isHighlightedMember(
  member: { email?: string; external?: boolean },
  orgDomains: string[] | undefined
): boolean {
  const isExternal = member?.external ?? false;
  if (!isExternal) return true;

  const memberDomain = getDomainFromEmail(member?.email || "");
  return orgDomains?.some((domain) => domain === memberDomain) ?? false;
}

/**
 * Gets initials from a full_name property.
 * @param fullName - The source name.
 */
export function getUserInitialsFromFullName(fullName?: string) {
  if (!fullName?.trim().length) {
    return "";
  }
  const nameSplit = fullName.trim().split(" ").filter(Boolean);
  const firstInitial = nameSplit[0]?.substring(0, 1);
  const lastInitial = nameSplit.length > 1 ? nameSplit[nameSplit.length - 1]?.substring(0, 1) : "";
  return `${firstInitial}${lastInitial}`;
}
